import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

/**
 * Top-level route error boundary.
 *
 * Catches any error thrown by the lazy-loaded route tree — including chunk
 * load failures that the lazyWithRetry helper couldn't recover from after a
 * one-shot reload. Without this, a chunk-load rejection produces a blank
 * white screen because <Suspense> on its own does not handle import
 * rejections.
 *
 * Two CTAs:
 *   • Reload — bypasses any SPA-cached state, refetches index.html, picks up
 *     current chunk hashes. Fixes 99% of post-deploy chunk errors.
 *   • Go home — for non-chunk errors, escapes the broken route entirely.
 */
class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    const msg = error?.message ?? "";
    const name = error?.name ?? "";
    const isChunkError =
      name === "ChunkLoadError" ||
      /Loading chunk [\d]+ failed/i.test(msg) ||
      /Loading CSS chunk/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /dynamically imported module/i.test(msg);
    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("RouteErrorBoundary caught:", error, info);
  }

  private handleReload = () => {
    // Hard reload — ignores SPA cache, re-pulls index.html and fresh chunks.
    if (typeof window !== "undefined") window.location.reload();
  };

  private handleGoHome = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-5">
          <div className="mx-auto h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-destructive" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-foreground">
              {this.state.isChunkError
                ? "A new version is available"
                : "Something went wrong"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {this.state.isChunkError
                ? "We've shipped an update since you opened this tab. Reload to get the latest."
                : "An unexpected error stopped this page from rendering. Reload to try again."}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button onClick={this.handleReload} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Reload page
            </Button>
            <Button onClick={this.handleGoHome} variant="outline" className="gap-2">
              <Home className="h-4 w-4" />
              Go home
            </Button>
          </div>
          {/* Show error message in dev / for engineering support context */}
          {this.state.error?.message && (
            <details className="pt-4 text-left">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                Technical details
              </summary>
              <pre className="mt-2 text-[11px] text-muted-foreground/80 whitespace-pre-wrap break-words bg-muted/40 p-3 rounded-md">
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
